import { Link } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DeleteAccountPanel from "@/components/DeleteAccountPanel";

/** Public page describing how to delete a Metazik account — the web link
 * Google Play requires alongside the in-app deletion. Reachable without
 * being logged in. */
export default function DeleteAccountPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/auth" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8 transition">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 grid place-items-center rounded-xl bg-gradient-to-br from-[#FF0050] to-[#8A2BE2]">
            <Trash2 className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-3xl font-black font-display">Supprimer mon compte <span translate="no">Metazik</span></h1>
        </div>

        <div className="space-y-6 text-zinc-300 leading-relaxed">
          <h2 className="text-xl font-bold text-white">Comment faire</h2>
          <ol className="list-decimal pl-6 space-y-1">
            <li>Connecte-toi à ton compte (site ou application).</li>
            <li>Ouvre ton profil et touche le crayon (modifier le profil).</li>
            <li>En bas, choisis « Supprimer mon compte » et confirme avec ton mot de passe.</li>
          </ol>
          <p>
            La suppression est immédiate et définitive. Si tu ne peux plus te connecter, écris-nous à{" "}
            <strong>equipemetazik@gmail.com</strong> depuis l'adresse email de ton compte.
          </p>

          <h2 className="text-xl font-bold text-white">Ce qui est supprimé</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Ton profil, ton adresse email et ton mot de passe.</li>
            <li>Tes publications, stories, commentaires, likes, abonnements, playlists et notifications.</li>
            <li>Pour un artiste : ses événements, ses lives, ses défis, et les morceaux que personne n'a achetés (avec leurs fichiers).</li>
            <li>Tes photos et fichiers envoyés.</li>
          </ul>

          <h2 className="text-xl font-bold text-white">Ce qui est conservé</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Les morceaux déjà achetés par des fans.</strong> Si un artiste supprime son compte, les fans qui ont acheté
              ses morceaux les gardent dans leur bibliothèque et peuvent toujours les écouter (avec le nom d'artiste comme crédit).
              Ces morceaux ne sont simplement plus en vente.
            </li>
            <li>
              L'historique des achats et des paiements, pour la comptabilité et les obligations légales, sans numéro de
              téléphone, email ni nom.
            </li>
          </ul>

          <h2 className="text-xl font-bold text-white">Avant de supprimer</h2>
          <p>
            Retire tes gains : un solde non retiré (gains de ventes ou Notes) est perdu à la suppression, et le système te demandera
            de le confirmer explicitement. Un retrait ou un paiement en cours de traitement bloque la suppression
            jusqu'à son terme.
          </p>

          <div className="pt-4 border-t border-white/10">
            {user ? (
              <DeleteAccountPanel />
            ) : (
              <Link to="/auth" className="btn-primary inline-flex items-center gap-2">
                Me connecter pour supprimer mon compte
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
